import { z, type ZodType } from '@customerio/hydra-validation';
import type { EnvironmentsGetEnvironmentResponse } from '@customerio/hydra-ui-api';
import { defineEntity } from '../shared/define-entity';

/**
 * Workspace environment — a per-workspace singleton aggregate. The
 * `GET /environments/{id}` response bundles the environment record with its
 * sideloaded `identities` and `object_types`, and consumers read all three,
 * so the entity wraps the whole response rather than just the bare
 * `Environment` model.
 *
 * Minimal detail-only entity: the read schema is a passthrough today (no
 * field needs normalization for current consumers). A list/mutation surface
 * can be added when needed.
 */
export type WorkspaceEnvironment = EnvironmentsGetEnvironmentResponse;

// oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
const EnvironmentFromApi = z.looseObject({}) as unknown as ZodType<WorkspaceEnvironment, unknown>;

/** @public */
export const environment = defineEntity<WorkspaceEnvironment>({
  name: 'environment',
  scope: 'workspace',
  // One environment per workspace; key by its own id when present.
  pk: (env) => String(env.environment?.id ?? 'current'),
  schema: { read: EnvironmentFromApi },
});

export const environmentKeys = {
  ...environment.keys,
  /** Singleton slot for the workspace's environment aggregate. */
  current: (workspaceId: number) => [...environment.keys.all(workspaceId), 'current'] as const,
};

export function parseEnvironment(raw: EnvironmentsGetEnvironmentResponse): WorkspaceEnvironment {
  return environment.parse(raw);
}
